import * as THREE from 'three';
import { shaderMaterial } from '@react-three/drei';
import { extend, ThreeElement } from '@react-three/fiber';

export interface CircleDashUniforms {
  boxMin: THREE.Vector2;
  boxMax: THREE.Vector2;
  centerUV: THREE.Vector2;
  radiusUV: THREE.Vector2;
  thickness: number;
  dashCount: number;
  dashRatio: number;
  color: THREE.Color;
  opacity: number;
}

export const CircleDashMaterial = /* @__PURE__ */ shaderMaterial(
  {
    boxMin: new THREE.Vector2(0, 0),
    boxMax: new THREE.Vector2(1, 1),
    centerUV: new THREE.Vector2(0.5, 0.5),
    radiusUV: new THREE.Vector2(0.1, 0.1),
    thickness: 0.02,
    dashCount: 32,
    dashRatio: 0.5,
    color: new THREE.Color(0x0b4f6c),
    opacity: 1,
  },

  `
  varying vec2 vUV;
  uniform vec2 boxMin, boxMax;

  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vUV = (worldPos.xz - boxMin) / (boxMax - boxMin);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
  `,
  // fragment shader
  `
  #define PI 3.141592653589793

  varying vec2 vUV;
  uniform vec2 centerUV;
  uniform vec2  radiusUV;      // = (radius/sizeX, radius/sizeZ)
  uniform float thickness; // = thickness / radius
  uniform float dashCount, dashRatio;
  uniform vec3  color;
  uniform float opacity;

  void main(){
    vec2 d = vUV - centerUV;
    float nd = length(d / radiusUV);

    float halfT = thickness * 0.5;
    float in0  = 1.0 - halfT;
    float in1  = in0 + 0.001;
    float out0 = 1.0 + halfT;
    float out1 = out0 + 0.001;

    float edge = smoothstep(in0, in1, nd)
                - smoothstep(out0, out1, nd);

    float ang    = (atan(d.y, d.x) + PI) / (2.0 * PI);
    float seg    = ang * dashCount;
    float dashOn = step(fract(seg), dashRatio);
    float alpha  = edge * dashOn;

    if(alpha < 1e-3) discard;
    gl_FragColor = vec4(color, alpha * opacity);
  }
  `,
);

declare module '@react-three/fiber' {
  interface ThreeElements {
    circleDashMaterial: ThreeElement<typeof CircleDashMaterial>;
  }
}

extend({ CircleDashMaterial });
